// src/pages/LinkedInCallback.jsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";

const ACCOUNT_EXISTS_GOOGLE_MSG =
  "An account with this email already exists using Google Sign-In. Please log in with Google.";

function readCallbackParams() {
  const search = new URLSearchParams(window.location.search || "");
  const rawHash = (window.location.hash || "").replace(/^#/, "");
  const hashQuery = rawHash.includes("?") ? rawHash.split("?")[1] : rawHash;
  const hash = new URLSearchParams(hashQuery || "");

  const get = (key) => {
    const fromSearch = search.get(key);
    if (fromSearch) return fromSearch;
    return hash.get(key);
  };

  return {
    code: get("code"),
    state: get("state"),
    error: get("error"),
    errorDescription: get("error_description"),
  };
}

export default function LinkedInCallback() {
  const navigate = useNavigate();
  const { completeLinkedInSignIn } = useAuth();
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function finish() {
      const { code, state, error: oauthError, errorDescription } = readCallbackParams();

      if (errorDescription || oauthError) {
        setError(errorDescription || oauthError);
        return;
      }

      if (!code) {
        setError("Missing LinkedIn authorization code.");
        return;
      }

      try {
        const { needsOnboarding } = await completeLinkedInSignIn({ code, state });
        if (cancelled) return;
        navigate(needsOnboarding ? "/complete-profile" : "/", { replace: true });
      } catch (err) {
        if (cancelled) return;
        if (err?.code === "auth/account-exists-with-different-credential") {
          setError(ACCOUNT_EXISTS_GOOGLE_MSG);
        } else if (err?.code === "functions/not-found") {
          setError("LinkedIn sign-in service is not deployed yet. Deploy Cloud Functions and try again.");
        } else {
          setError(err?.message || "LinkedIn sign-in failed. Please try again.");
        }
      }
    }

    finish();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="auth-page">
      <div className="card auth-card" style={{ textAlign: "center" }}>
        {error ? (
          <>
            <h1 style={{ fontSize: 22, marginBottom: 10 }}>LinkedIn sign-in failed</h1>
            <p style={{ fontSize: 14, color: "var(--text-2)", lineHeight: 1.5, marginBottom: 18 }}>
              {error}
            </p>
            <Link to="/login" className="btn btn-primary" style={{ justifyContent: "center" }}>
              Back to sign in
            </Link>
          </>
        ) : (
          <>
            <div className="spinner" style={{ width: 28, height: 28, margin: "0 auto 14px" }} />
            <h1 style={{ fontSize: 22, marginBottom: 8 }}>Signing you in…</h1>
            <p style={{ fontSize: 14, color: "var(--text-3)" }}>
              Completing LinkedIn authentication
            </p>
          </>
        )}
      </div>
    </div>
  );
}
